import clsx from "clsx";
import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { daysOfWeek, getCalendarDates, getISODate } from "../../utils/date";
import LinearLoader from "../loading/LinearLoader/LinearLoader";
import WorkoutResultsService from "../../services/WorkoutResultsService";
import {
  BodypartType,
  CompletedExercisesWithAssetDto,
  MovementPatternType,
  UserExerciseDto,
} from "@growth-machine-llc/stridist-api-client";
import dayjs from "dayjs";
import makeStyles from "@mui/styles/makeStyles";
import CalendarDay from "./workout-activity-calendar/CalendarDay";
import CalendarDayInfo from "./workout-activity-calendar/CalendarDayInfo";
import { BodypartTypeLabels } from "../exercise-library/constants";
import { alpha } from "@mui/material/styles";

export const createCrossLinesGradient = (color: string) =>
  `repeating-linear-gradient(
    135deg,
    ${alpha(color, 0.3)} 0px,
    ${alpha(color, 0.3)} 2px,
    transparent 3px,
    transparent 7px
  )`;

export const createDotsPatternStaggeredSingle = (
  color: string,
  dot = 2,
  gap = 8,
) =>
  `radial-gradient(circle, ${color} ${dot}px, transparent ${dot}px) 0 0 / ${gap}px ${gap}px,
   radial-gradient(circle, ${color} ${dot}px, transparent ${dot}px) ${gap / 2}px ${gap / 2}px / ${gap}px ${gap}px`;

const useStyles = makeStyles((theme) => ({
  weekDay: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  disabled: {
    color: theme.palette.text.disabled,
    pointerEvents: "none",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  legendCircle: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "inline-block",
    flexShrink: 0,
  },
  legendCircleCurrentBodypart: {
    backgroundImage: createCrossLinesGradient(theme.palette.primary.main),
  },
  legendCircleOtherExercises: {
    background: createDotsPatternStaggeredSingle(theme.palette.text.disabled),
  },
  legendCircleCurrent: {
    backgroundColor: theme.palette.primary.main,
  },

  dayInfo: {
    border: "none",
    width: "auto",
    maxWidth: "unset",
    padding: 0,
  },
  divider: {
    margin: theme.spacing(2, 0, 1),
  },
}));

interface ScheduleDate {
  date: string;
  id?: number;
  title?: string;
  selected?: boolean;
  bodyparts?: BodypartType[];
  movementPatterns?: MovementPatternType[];
  content?: string;
}

interface WorkoutActivityCalendarProps {
  handleDayClick?: (date: string) => void;
  clientId: number;
  exerciseAssetId: number;
  bodypart: BodypartType;
  date?: string;
  onExerciseSelect?: (exercise: UserExerciseDto) => void;
}

export const CLIENT_ACTIVITY_CALENDAR_QUERY_KEY = "client-activity-calendar";

function getMonthRange(year: number, month: number, intervalSize = 1) {
  const startDate = new Date(year, month - intervalSize, 1);
  const endDate = new Date(year, month + intervalSize, 0);
  return {
    start: getISODate(startDate),
    end: getISODate(endDate),
  };
}

const WorkoutActivityCalendar = (props: WorkoutActivityCalendarProps) => {
  const { clientId, date, onExerciseSelect, exerciseAssetId, bodypart } = props;

  const s = useStyles();

  const detailsRef = React.useRef<HTMLDivElement | null>(null);
  // use when details opened from the tooltip
  const [shouldScrollToDetails, setShouldScrollToDetails] =
    React.useState(false);
  const [expandExerciseDetails, setExpandExerciseDetails] =
    React.useState(false);

  const [selectedDayItem, setSelectedDayItem] = useState<any | null>(null);

  const year = dayjs(date).year();
  const month = dayjs(date).month();

  const { start, end } = getMonthRange(year, month, 1);

  const { data: summary, isFetching } = useQuery({
    queryKey: [CLIENT_ACTIVITY_CALENDAR_QUERY_KEY, { clientId }],
    queryFn: () =>
      WorkoutResultsService.getCompletedExercisesByDateRanges(clientId),
  });

  const schedule: ScheduleDate[] = React.useMemo(() => {
    if (!summary) return [];
    return summary.flatMap((item: CompletedExercisesWithAssetDto) =>
      item.assetId
        ? [
            {
              id: item.assetId,
              title: item.assetName,
              date: item.completedAt
                ? item.completedAt.format("YYYY-MM-DD")
                : "",
              bodyparts: item.bodyparts,
              movementPatterns: item.movementPatterns,
              content: item.content,
              coachId: item.coachId,
            },
          ]
        : [],
    );
  }, [summary]);

  const calendar = React.useMemo(() => {
    return getCalendarDates(year, month).map((date) => {
      const isoDate = getISODate(date);
      const dayExercises = schedule.filter((s) => s.date === isoDate);

      const matchingExercises = dayExercises.filter((exercise) =>
        exercise.bodyparts?.includes(bodypart),
      );

      const hasMatchingBodypart = matchingExercises.length > 0;

      const completedCurrentExercise = dayExercises.some(
        (s) => s.id === exerciseAssetId,
      );

      const hasOtherMatchingExercises = matchingExercises.some(
        (s) => s.id !== exerciseAssetId,
      );

      const hasOtherBodypartExercises =
        dayExercises.length > 0 &&
        !completedCurrentExercise &&
        !hasOtherMatchingExercises;

      const isCurrentMonth = date.getMonth() === month;

      return {
        date: isoDate,
        day: date.getDate(),
        assets: dayExercises,
        titles: dayExercises.flatMap((s) => [s.title]),
        isCurrentMonth,
        hasMatchingBodypart,
        completedCurrentExercise,
        hasOtherMatchingExercises,
        hasOtherBodypartExercises,
        matchingExercises,
        bodyparts: dayExercises.flatMap((s) => s.bodyparts ?? []),
        movementPatterns: dayExercises.flatMap((s) => s.movementPatterns ?? []),
      };
    });
  }, [year, month, schedule, exerciseAssetId, bodypart]);

  const isLoadingInitial = isFetching;

  const legendItems = [
    { label: "Current exercise", className: s.legendCircleCurrent },
    {
      label: `${BodypartTypeLabels[bodypart]} exercises`,
      className: s.legendCircleCurrentBodypart,
    },
    { label: "Other exercises", className: s.legendCircleOtherExercises },
  ];

  const handleSelectDay = React.useCallback(
    (dayItem: any) => {
      setSelectedDayItem(
        selectedDayItem?.date === dayItem.date ? null : dayItem,
      );

      !expandExerciseDetails && setShouldScrollToDetails(true);
    },
    [selectedDayItem],
  );

  const handleShowMore = (dayItem: any) => {
    setSelectedDayItem(dayItem);
    setShouldScrollToDetails(true);
    setExpandExerciseDetails(true);
  };

  React.useEffect(() => {
    if (shouldScrollToDetails && detailsRef.current) {
      detailsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
      setShouldScrollToDetails(false);
      setExpandExerciseDetails(false);
    }
  }, [shouldScrollToDetails]);

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          mb: 1,
          gap: 1.5,
        }}
      >
        {daysOfWeek.map((day, idx) => (
          <Typography key={idx} align="center" className={s.weekDay}>
            {day}
          </Typography>
        ))}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 1.5,
        }}
      >
        {calendar.map((dayItem, index) => (
          <CalendarDay
            key={index}
            dayItem={dayItem}
            isLoadingInitial={isLoadingInitial}
            isSelected={selectedDayItem?.date === dayItem.date}
            detailsOpened={!!selectedDayItem}
            selectedBodypart={bodypart}
            onExerciseSelect={onExerciseSelect}
            onDaySelect={handleSelectDay}
            onShowMore={handleShowMore}
          />
        ))}
      </Box>
      {isFetching && (
        <Box sx={{ width: "100%", height: 4, pt: 2, px: 4 }}>
          <LinearLoader />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-evenly",
          mt: 2,
        }}
      >
        {legendItems.map((item) => (
          <Box key={item.label} className={s.legendItem}>
            <span className={clsx(s.legendCircle, item.className)} />
            <Typography
              variant="body2"
              sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
      {selectedDayItem && (
        <>
          <Divider className={s.divider} />
          <CalendarDayInfo
            dayItem={selectedDayItem}
            selectedBodypart={bodypart}
            className={s.dayInfo}
            exerciseListInitialExpanded={expandExerciseDetails}
            onClose={() => setSelectedDayItem(null)}
            formattedDate={dayjs(selectedDayItem.date).format("MMMM D, YYYY")}
            onExerciseSelect={onExerciseSelect}
            isTooltip={false}
          />
          <div ref={detailsRef} />
        </>
      )}
    </>
  );
};

export default WorkoutActivityCalendar;
